<template>
    <main class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-y-auto" tabindex="0">
        <form @submit.prevent="saveModule">
            <div class="px-8 sm:px-8 lg:px-8 pt-6 pb-2">
                <div class="w-full flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                    <div class="flex-1 min-w-0">
                        <h1 class="stage-page-title">{{ isEdit ? $t('Modul bearbeiten') : $t('Modul anlegen') }}</h1>
                    </div>
                    <div class="inline-flex items-center">
                        <div
                            v-if="isEdit"
                            ref="headerActionMenuWrapper"
                            class="relative inline-flex items-stretch mr-0"
                        >
                            <button class="btn-stage-primary !rounded-r-none !pr-3.5 inline-flex items-center gap-1.5" type="submit" :disabled="busy">
                                <span>{{ $t('Save') }}</span>
                                <span
                                    class="h-2 w-2 flex-shrink-0 rounded-full"
                                    :class="[publicationStateDotClass, showPublicationStateDot ? '' : 'opacity-0']"
                                    :title="publicationStateLabel"
                                    :aria-label="publicationStateLabel"
                                ></span>
                            </button>
                            <button
                                ref="headerActionMenuTrigger"
                                class="btn-stage-primary !rounded-l-none border-l border-l-[rgba(255,255,255,0.16)] min-w-[2.25rem] !px-2 inline-flex items-center justify-center"
                                type="button"
                                :title="$t('Aktionen')"
                                :aria-label="$t('Aktionen')"
                                :aria-expanded="headerActionMenuOpen ? 'true' : 'false'"
                                :disabled="busy"
                                @click.stop="toggleHeaderActionMenu"
                            >
                                <svg-vue class="h-4 w-4" icon="font-awesome.regular.ellipsis-vertical"></svg-vue>
                            </button>
                            <div
                                v-show="headerActionMenuOpen"
                                ref="headerActionMenu"
                                class="absolute right-0 top-full z-20 mt-2 whitespace-nowrap rounded border border-bordergray-100 bg-white stage-dropdown-menu shadow-lg"
                            >
                                <button class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50" type="button" :disabled="busy" @click.stop="runHeaderAction('publish')">
                                    {{ $t('Veröffentlichen') }}
                                </button>
                                <button v-if="module.published_at" class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50" type="button" :disabled="busy" @click.stop="runHeaderAction('reset')">
                                    {{ $t('Zurücksetzen (letzte Veröffentlichung)') }}
                                </button>
                                <button class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50" type="button" :disabled="busy" @click.stop="runHeaderAction('cancel')">
                                    {{ $t('Abbrechen') }}
                                </button>
                                <button class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50" type="button" :disabled="busy" @click.stop="runHeaderAction('delete')">
                                    {{ $t('Modul löschen') }}
                                </button>
                            </div>
                        </div>
                        <button v-else class="btn-stage-primary" type="submit" :disabled="busy">
                            {{ $t('Modul anlegen') }}
                        </button>
                    </div>
                </div>
            </div>

            <div class="stage-page-body">
                <loading :status="loading"/>

                <div class="space-y-6">
                    <app-panel body-class="px-6 py-5">
                        <template #header>
                            <template v-if="isEdit">
                                <div class="flex flex-wrap items-center gap-x-3 gap-y-2 text-base text-gray-600">
                                    <template v-for="(crumb, index) in breadcrumbItems" :key="crumb.key">
                                        <span v-if="index > 0" class="text-gray-300">/</span>
                                        <router-link
                                            v-if="crumb.to"
                                            :to="crumb.to"
                                            class="inline-flex items-center gap-2 text-base text-gray-600 transition-colors hover:text-gray-900"
                                            :aria-label="crumb.label"
                                            :title="crumb.label"
                                        >
                                            <svg-vue
                                                v-if="crumb.icon"
                                                :icon="crumb.icon"
                                                class="h-4 w-4 flex-shrink-0 text-gray-400"
                                            />
                                            <span v-if="!crumb.iconOnly">{{ crumb.label }}</span>
                                            <span
                                                v-if="crumb.showStatus && !crumb.iconOnly"
                                                class="h-2 w-2 flex-shrink-0 rounded-full"
                                                :class="crumb.statusDotClass"
                                                :title="crumb.statusLabel"
                                                :aria-label="crumb.statusLabel"
                                            ></span>
                                        </router-link>
                                        <span
                                            v-else
                                            class="inline-flex items-center gap-2 text-base text-gray-700"
                                        >
                                            <svg-vue
                                                v-if="crumb.icon"
                                                :icon="crumb.icon"
                                                class="h-4 w-4 flex-shrink-0 text-gray-500"
                                            />
                                            <span>{{ crumb.label }}</span>
                                            <span
                                                v-if="crumb.showStatus"
                                                class="h-2 w-2 flex-shrink-0 rounded-full"
                                                :class="crumb.statusDotClass"
                                                :title="crumb.statusLabel"
                                                :aria-label="crumb.statusLabel"
                                            ></span>
                                        </span>
                                    </template>
                                </div>
                            </template>
                        </template>
                            <template v-if="isEdit">
                                <div class="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_auto] gap-6 items-end">
                                    <div>
                                        <label class="stage-filter-label block mb-2">{{ $t('Name') }}</label>
                                        <input v-model="module.name" class="stage-filter-input form-input block w-full text-base" required>
                                    </div>
                                    <div class="flex items-center xl:pb-2">
                                        <input-switch
                                            id="cms-module-active"
                                            v-model="module.is_active"
                                            :disabled-label="$t('Aktiviert')"
                                            :enabled-label="$t('Aktiviert')"
                                        />
                                    </div>
                                </div>
                            </template>
                            <div v-else class="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-6 items-end">
                                <div>
                                    <label class="stage-filter-label block mb-2">{{ $t('Modul-Preset') }}</label>
                                    <select
                                        v-model="module.cms_module_type_id"
                                        class="stage-filter-input form-select block w-full text-sm"
                                        required
                                        @change="applyModuleTypeDefaults"
                                    >
                                        <option v-for="moduleType in rootModuleTypeOptions" :key="moduleType.id" :value="moduleType.id">
                                            {{ moduleType.name }}
                                        </option>
                                    </select>
                                </div>
                                <div>
                                    <label class="stage-filter-label block mb-2">{{ $t('Name') }}</label>
                                    <input v-model="module.name" class="stage-filter-input form-input block w-full text-sm" required>
                                </div>
                                <div class="flex items-center xl:pb-2">
                                    <input-switch
                                        id="cms-module-active"
                                        v-model="module.is_active"
                                        :disabled-label="$t('Aktiviert')"
                                        :enabled-label="$t('Aktiviert')"
                                    />
                                </div>
                            </div>
                        <template v-if="isEdit" #footer>
                            <div class="text-sm text-gray-500 whitespace-nowrap">
                                {{ $t('Modul-Preset') }}: {{ activeModuleTypeName }}
                            </div>
                        </template>
                    </app-panel>

                    <app-panel v-if="settingsSchema.length > 0" :title="$t('Konfiguration')">
                            <div class="grid grid-cols-1 xl:grid-cols-2 gap-6">
                                <div
                                    v-for="field in settingsSchema"
                                    :key="'settings-' + field.key"
                                    :class="fieldColumnClass(field)"
                                            >
                                                <div class="mb-2 flex items-center justify-between gap-3">
                                                    <label class="stage-filter-label block">{{ field.label || field.key }}</label>
                                                    <button
                                                        v-if="field.type === 'html'"
                                                        class="inline-flex cursor-pointer items-center justify-center text-gray-400 transition hover:text-gray-700"
                                                        type="button"
                                                        :title="$t('Datei einfügen')"
                                                        :aria-label="$t('Datei einfügen')"
                                                        @click="openSettingsHtmlMediaDrawer(field.key)"
                                                    >
                                                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.image"></svg-vue>
                                                    </button>
                                                </div>
                                                <template v-if="field.type === 'wysiwyg'">
                                                    <cms-rich-text-editor
                                                        v-model="module.settings[field.key]"
                                            :id="fieldEditorId('settings', field.key)"
                                            :min-height="fieldMinHeight(field, 320)"
                                            :source-min-height="fieldMinHeight(field, 320)"
                                        />
                                                </template>
                                                <template v-else-if="field.type === 'html'">
                                                    <cms-html-code-editor
                                                        :ref="'settingsHtmlEditor_' + field.key"
                                                        v-model="module.settings[field.key]"
                                                        :min-height="fieldMinHeight(field, 320)"
                                                    />
                                    </template>
                                    <template v-else-if="isWrapperClassField(field)">
                                        <textarea
                                            v-model="module.settings[field.key]"
                                            class="stage-filter-input form-textarea block w-full text-sm"
                                            rows="3"
                                        ></textarea>
                                    </template>
                                    <template v-else-if="field.type === 'textarea'">
                                        <textarea
                                            v-model="module.settings[field.key]"
                                            class="stage-filter-input form-textarea block w-full text-sm"
                                            :rows="fieldRows(field, 4)"
                                        ></textarea>
                                    </template>
                                    <template v-else-if="field.type === 'file_picker'">
                                        <cms-file-picker-field v-model="module.settings[field.key]" />
                                    </template>
                                    <template v-else-if="field.type === 'select'">
                                        <select v-model="module.settings[field.key]" class="stage-filter-input form-select block w-full text-sm">
                                            <option v-for="option in fieldOptions(field)" :key="field.key + '-' + option.value" :value="option.value">
                                                {{ option.label }}
                                            </option>
                                        </select>
                                    </template>
                                    <template v-else-if="field.type === 'checkbox' || field.type === 'switch'">
                                        <div class="pt-2">
                                            <input-switch
                                                :id="'module-setting-' + field.key"
                                                v-model="module.settings[field.key]"
                                                :disabled-label="field.label || field.key"
                                                :enabled-label="field.label || field.key"
                                            />
                                        </div>
                                    </template>
                                    <template v-else>
                                        <input
                                            v-model="module.settings[field.key]"
                                            :type="field.type === 'url' ? 'url' : 'text'"
                                            class="stage-filter-input form-input block w-full text-sm"
                                        >
                                    </template>
                                </div>
                            </div>
                    </app-panel>

                    <app-panel v-if="translationSchema.length > 0" :title="$t('Sprachvarianten')" body-class="!p-0">
                            <div v-if="module.translations.length > 0" class="flex flex-col">
                                <div class="flex flex-wrap items-center gap-0 border-b border-gray-100 px-5">
                                    <button
                                        v-for="(translation, index) in module.translations"
                                        :key="translation.cms_language_id"
                                        type="button"
                                        class="-mb-px appearance-none border-0 border-b-2 bg-transparent px-3.5 pb-3 pt-3.5 text-sm font-medium leading-5 transition-colors duration-150 hover:text-gray-700"
                                        :class="activeTranslationIndex === index ? 'border-b-gray-700 text-gray-800' : 'border-b-transparent text-gray-500'"
                                        @click="activeTranslationIndex = index"
                                    >
                                        {{ translationLabel(translation, index) }}
                                    </button>
                                </div>

                                <div v-if="activeTranslation" class="cms-translation-content">
                                    <div class="cms-translation-meta">
                                        <div class="flex items-center justify-end">
                                            <input-switch
                                                :id="'cms-module-translation-active-' + activeTranslationIndex"
                                                v-model="activeTranslation.is_active"
                                                :disabled-label="$t('Aktiviert')"
                                                :enabled-label="$t('Aktiviert')"
                                            />
                                        </div>
                                    </div>
                                    <div class="p-6">
                                        <div class="grid grid-cols-1 xl:grid-cols-2 gap-6">
                                            <div
                                                v-for="field in translationSchema"
                                                :key="'translation-' + field.key"
                                                :class="fieldColumnClass(field)"
                                            >
                                                <div class="mb-2 flex items-center justify-between gap-3">
                                                    <label class="stage-filter-label block">{{ field.label || field.key }}</label>
                                                    <button
                                                        v-if="field.type === 'html'"
                                                        class="inline-flex cursor-pointer items-center justify-center text-gray-400 transition hover:text-gray-700"
                                                        type="button"
                                                        :title="$t('Datei einfügen')"
                                                        :aria-label="$t('Datei einfügen')"
                                                        @click="openTranslationHtmlMediaDrawer(activeTranslation.cms_language_id, field.key)"
                                                    >
                                                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.image"></svg-vue>
                                                    </button>
                                                </div>
                                                <template v-if="field.type === 'wysiwyg'">
                                                    <cms-rich-text-editor
                                                        v-model="activeTranslation.values[field.key]"
                                                        :id="fieldEditorId('translation-' + activeTranslation.cms_language_id, field.key)"
                                                        :min-height="fieldMinHeight(field, 320)"
                                                        :source-min-height="fieldMinHeight(field, 320)"
                                                    />
                                                </template>
                                                <template v-else-if="field.type === 'html'">
                                                    <cms-html-code-editor
                                                        :ref="'translationHtmlEditor_' + activeTranslation.cms_language_id + '_' + field.key"
                                                        v-model="activeTranslation.values[field.key]"
                                                        :min-height="fieldMinHeight(field, 320)"
                                                    />
                                                </template>
                                                <template v-else-if="isWrapperClassField(field)">
                                                    <textarea
                                                        v-model="activeTranslation.values[field.key]"
                                                        class="stage-filter-input form-textarea block w-full text-sm"
                                                        rows="3"
                                                    ></textarea>
                                                </template>
                                                <template v-else-if="field.type === 'textarea'">
                                                    <textarea
                                                        v-model="activeTranslation.values[field.key]"
                                                        class="stage-filter-input form-textarea block w-full text-sm"
                                                        :rows="fieldRows(field, 4)"
                                                    ></textarea>
                                                </template>
                                                <template v-else-if="field.type === 'file_picker'">
                                                    <cms-file-picker-field v-model="activeTranslation.values[field.key]" />
                                                </template>
                                                <template v-else-if="field.type === 'select'">
                                                    <select v-model="activeTranslation.values[field.key]" class="stage-filter-input form-select block w-full text-sm">
                                                        <option v-for="option in fieldOptions(field)" :key="field.key + '-' + option.value" :value="option.value">
                                                            {{ option.label }}
                                                        </option>
                                                    </select>
                                                </template>
                                                <template v-else-if="field.type === 'checkbox' || field.type === 'switch'">
                                                    <div class="pt-2">
                                                        <input-switch
                                                            :id="'module-translation-' + field.key + '-' + activeTranslationIndex"
                                                            v-model="activeTranslation.values[field.key]"
                                                            :disabled-label="field.label || field.key"
                                                            :enabled-label="field.label || field.key"
                                                        />
                                                    </div>
                                                </template>
                                                <template v-else>
                                                    <input
                                                        v-model="activeTranslation.values[field.key]"
                                                        :type="field.type === 'url' ? 'url' : 'text'"
                                                        class="stage-filter-input form-input block w-full text-sm"
                                                    >
                                                </template>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                    </app-panel>

                    <cms-slot-table
                        v-for="group in module.slot_groups"
                        :key="group.slot"
                        :group="group"
                        :context-id="module.id"
                        context-type="module"
                        :module-type-options="moduleTypeOptions"
                        :navigation-context="slotNavigationContext"
                        @changed="getModule"
                        @reordered="applySlotReorder"
                    />
                </div>
            </div>
        </form>

        <confirm-dialog
            :open="confirmDialog.open"
            :title="confirmDialog.title"
            :text="confirmDialog.text"
            :headline-id="'cms-module-confirm-modal-headline'"
            @close="closeConfirmDialog"
            @confirm="confirmDialogAction"
        />
    </main>
</template>

<script>
import CmsSlotTable from "@/components/cms/cms-slot-table";
import ConfirmDialog from "@/components/modals/confirm-dialog";
import CmsHtmlCodeEditor from "@/components/forms/cms-html-code-editor";
import CmsFilePickerField from "@/components/forms/cms-file-picker-field";
import CmsRichTextEditor from "@/components/forms/cms-rich-text-editor";

export default {
    name: "cms-module-editor",
    components: {
        CmsHtmlCodeEditor,
        CmsFilePickerField,
        CmsRichTextEditor,
        CmsSlotTable,
        ConfirmDialog,
    },
    data() {
        return {
            loading: false,
            confirmDialog: {
                open: false,
                title: '',
                text: '',
                pendingAction: null,
            },
            busy: false,
            busyAction: null,
            headerActionMenuOpen: false,
            moduleStateResolved: false,
            moduleTypeOptions: [],
            languageOptions: [],
            originEntity: null,
            breadcrumbModules: [],
            activeTranslationIndex: 0,
            module: {
                id: null,
                cms_module_type_id: null,
                name: '',
                is_global: true,
                is_active: true,
                settings: {},
                translations: [],
                slot_groups: [],
                module_type_definition: null,
                published_at: null,
                has_unpublished_changes: false,
            },
        };
    },
    computed: {
        isEdit() {
            return !!this.$route.params.id;
        },
        closeTarget() {
            if (this.parentModulePath.length > 0) {
                const parentId = this.parentModulePath[this.parentModulePath.length - 1];
                const ancestorPath = this.parentModulePath.slice(0, -1);
                const query = {};

                if (this.originPath) {
                    query.origin_path = this.originPath;
                }

                if (ancestorPath.length > 0) {
                    query.module_path = ancestorPath.join(',');
                }

                return {
                    path: '/workspace/cms/module-library/' + parentId + '/edit',
                    query,
                };
            }

            return this.originPath || '/workspace/cms/module-library';
        },
        routeContext() {
            const originPath = typeof this.$route.query.origin_path === 'string'
                ? this.$route.query.origin_path.trim()
                : '';
            const modulePath = this.parseModulePath(this.$route.query.module_path);
            const legacyReturnTo = typeof this.$route.query.return_to === 'string'
                ? this.$route.query.return_to.trim()
                : '';

            if (originPath !== '' || modulePath.length > 0) {
                return {
                    originPath: originPath || '/workspace/cms/module-library',
                    modulePath,
                };
            }

            if (legacyReturnTo !== '') {
                return this.parseLegacyRouteContext(legacyReturnTo);
            }

            return {
                originPath: '/workspace/cms/module-library',
                modulePath: [],
            };
        },
        originPath() {
            return this.routeContext.originPath;
        },
        parentModulePath() {
            return this.routeContext.modulePath;
        },
        slotNavigationContext() {
            return {
                originPath: this.originPath,
                modulePath: this.module.id
                    ? [...this.parentModulePath, Number(this.module.id)]
                    : [...this.parentModulePath],
            };
        },
        originContext() {
            const path = String(this.originPath || '').trim();

            if (path === '') {
                return null;
            }

            let match = path.match(/\/workspace\/cms\/pages\/(\d+)\/edit/);
            if (match) {
                return {
                    type: 'page',
                    id: Number(match[1]),
                    overviewPath: '/workspace/cms/pages',
                    overviewLabel: this.$t('Seiten'),
                    overviewIcon: 'font-awesome.regular.copy',
                    entityPath: path,
                    entityFallbackLabel: this.$t('Seite'),
                    entityIcon: 'font-awesome.regular.copy',
                    endpoint: 'api/workspace/admin/cms/pages/' + match[1],
                };
            }

            match = path.match(/\/workspace\/cms\/templates\/(\d+)\/edit/);
            if (match) {
                return {
                    type: 'template',
                    id: Number(match[1]),
                    overviewPath: '/workspace/cms/templates',
                    overviewLabel: this.$t('Templates'),
                    overviewIcon: 'font-awesome.regular.sidebar',
                    entityPath: path,
                    entityFallbackLabel: this.$t('Template'),
                    entityIcon: 'font-awesome.regular.sidebar',
                    endpoint: 'api/workspace/admin/cms/templates/' + match[1],
                };
            }

            match = path.match(/\/workspace\/cms\/module-library\/(\d+)\/edit/);
            if (match) {
                return {
                    type: 'module',
                    id: Number(match[1]),
                    overviewPath: '/workspace/cms/module-library',
                    overviewLabel: this.$t('Modul-Bibliothek'),
                    overviewIcon: 'font-awesome.regular.cubes',
                    entityPath: path,
                    entityFallbackLabel: this.$t('Modul'),
                    entityIcon: 'font-awesome.regular.cube',
                    endpoint: 'api/workspace/admin/cms/modules/' + match[1],
                };
            }

            return {
                type: 'fallback',
                id: null,
                overviewPath: path,
                overviewLabel: this.$t('Modul-Bibliothek'),
                overviewIcon: 'font-awesome.regular.cubes',
                entityPath: null,
                entityFallbackLabel: null,
                entityIcon: null,
                endpoint: null,
            };
        },
        parentBreadcrumbs() {
            return this.breadcrumbModules.map((module, index) => {
                const ancestorPath = this.parentModulePath.slice(0, index);
                const query = {};

                if (this.originPath) {
                    query.origin_path = this.originPath;
                }

                if (ancestorPath.length > 0) {
                    query.module_path = ancestorPath.join(',');
                }

                return {
                    id: module.id,
                    label: module.name,
                    icon: 'font-awesome.regular.cube',
                    showStatus: true,
                    statusSource: module,
                    to: {
                        path: '/workspace/cms/module-library/' + module.id + '/edit',
                        query,
                    },
                };
            });
        },
        breadcrumbItems() {
            const withStatus = (item) => {
                if (!item?.showStatus) {
                    return item;
                }

                return {
                    ...item,
                    statusDotClass: this.contentStatusDotClass(item.statusSource),
                    statusLabel: this.contentStatusLabel(item.statusSource),
                };
            };

            const items = [];
            const originContext = this.originContext;

            if (originContext) {
                items.push({
                    key: 'overview',
                    label: originContext.overviewLabel,
                    icon: originContext.overviewIcon,
                    to: originContext.overviewPath,
                    iconOnly: true,
                });

                if (originContext.entityPath) {
                    items.push(withStatus({
                        key: 'origin-entity',
                        label: this.originEntity?.name || originContext.entityFallbackLabel,
                        icon: originContext.entityIcon,
                        to: originContext.entityPath,
                        showStatus: ['page', 'module'].includes(originContext.type),
                        statusSource: this.originEntity,
                    }));
                }
            }

            this.parentBreadcrumbs.forEach((crumb) => {
                items.push(withStatus({
                    key: 'module-' + crumb.id,
                    label: crumb.label,
                    icon: crumb.icon,
                    to: crumb.to,
                    showStatus: crumb.showStatus,
                    statusSource: crumb.statusSource,
                }));
            });

            items.push(withStatus({
                key: 'current-module',
                label: this.module.name || this.$t('Modul bearbeiten'),
                icon: 'font-awesome.regular.cube',
                showStatus: true,
                statusSource: this.module,
                to: null,
            }));

            return items;
        },
        publicationStateLabel() {
            if (!this.module.published_at) {
                return this.$t('Noch nicht veröffentlicht');
            }

            if (this.module.has_unpublished_changes) {
                return this.$t('Unveröffentlichte Änderungen');
            }

            return this.$t('Veröffentlicht');
        },
        publicationStateClass() {
            if (!this.module.published_at) {
                return 'text-gray-500';
            }

            return this.module.has_unpublished_changes ? 'text-amber-600' : 'text-green-600';
        },
        publicationStateDotClass() {
            return this.contentStatusDotClass(this.module);
        },
        showPublicationStateDot() {
            return !this.isEdit || this.moduleStateResolved;
        },
        activeModuleType() {
            if (this.module.module_type_definition && this.module.module_type_definition.id) {
                return this.module.module_type_definition;
            }

            return this.moduleTypeOptions.find((moduleType) => moduleType.id === this.module.cms_module_type_id) || null;
        },
        activeModuleTypeName() {
            return this.activeModuleType ? this.activeModuleType.name : '-';
        },
        childOnlyTypeKeys() {
            const childKeys = new Set();

            this.moduleTypeOptions.forEach((moduleType) => {
                (Array.isArray(moduleType.slot_schema) ? moduleType.slot_schema : []).forEach((slot) => {
                    (Array.isArray(slot.allowed_module_type_keys) ? slot.allowed_module_type_keys : []).forEach((key) => {
                        childKeys.add(key);
                    });
                });
            });

            return Array.from(childKeys);
        },
        rootModuleTypeOptions() {
            return this.moduleTypeOptions.filter((moduleType) => {
                return !this.childOnlyTypeKeys.includes(moduleType.key);
            });
        },
        settingsSchema() {
            return Array.isArray(this.activeModuleType?.settings_schema) ? this.activeModuleType.settings_schema : [];
        },
        translationSchema() {
            return Array.isArray(this.activeModuleType?.translation_schema) ? this.activeModuleType.translation_schema : [];
        },
        activeTranslation() {
            return this.module.translations[this.activeTranslationIndex] || null;
        },
    },
    mounted() {
        document.addEventListener('click', this.handleDocumentClick);
        this.normalizeRouteQuery();
        this.loadOptions().finally(() => {
            if (this.isEdit) {
                this.getModule();
            } else {
                this.module.cms_module_type_id = this.rootModuleTypeOptions[0]?.id || null;
                this.applyModuleTypeDefaults();
            }
        });
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleDocumentClick);
    },
    watch: {
        '$route.fullPath'(nextPath, previousPath) {
            if (nextPath !== previousPath) {
                this.normalizeRouteQuery();
            }

            if (nextPath !== previousPath && this.isEdit) {
                this.getModule();
            }
        },
    },
    methods: {
        resolveEditorRef(refName) {
            const ref = this.$refs[refName];

            if (Array.isArray(ref)) {
                return ref[0] || null;
            }

            return ref || null;
        },
        openSettingsHtmlMediaDrawer(fieldKey) {
            this.resolveEditorRef('settingsHtmlEditor_' + fieldKey)?.openMediaDrawer();
        },
        openTranslationHtmlMediaDrawer(languageId, fieldKey) {
            this.resolveEditorRef('translationHtmlEditor_' + languageId + '_' + fieldKey)?.openMediaDrawer();
        },
        closeHeaderActionMenu() {
            this.headerActionMenuOpen = false;
        },
        toggleHeaderActionMenu() {
            this.headerActionMenuOpen = !this.headerActionMenuOpen;
        },
        runHeaderAction(action) {
            this.closeHeaderActionMenu();

            if (action === 'publish') {
                this.publishModule();
                return;
            }

            if (action === 'reset') {
                this.resetModuleDraft();
                return;
            }

            if (action === 'cancel') {
                this.$router.push(this.closeTarget);
                return;
            }

            if (action === 'delete') {
                this.deleteModule();
            }
        },
        handleDocumentClick(event) {
            if (!this.headerActionMenuOpen) {
                return;
            }

            const menuElement = this.$refs.headerActionMenu;
            const triggerElement = this.$refs.headerActionMenuTrigger;
            const wrapperElement = this.$refs.headerActionMenuWrapper;

            if (!(menuElement && menuElement.contains(event.target))
                && !(triggerElement && triggerElement.contains(event.target))
                && !(wrapperElement && wrapperElement.contains(event.target))) {
                this.closeHeaderActionMenu();
            }
        },
        applySlotReorder(payload) {
            const slot = String(payload?.slot || '');
            const items = Array.isArray(payload?.items) ? payload.items : null;

            if (slot === '' || !items) {
                return;
            }

            const slotGroup = this.module.slot_groups.find((group) => group.slot === slot);
            if (!slotGroup) {
                return;
            }

            slotGroup.items = items;
        },
        contentStatusDotClass(entity) {
            if (!entity?.is_active) {
                return 'bg-red-500';
            }

            if (!entity?.published_at || entity?.has_unpublished_changes) {
                return 'bg-amber-500';
            }

            return 'bg-green-500';
        },
        contentStatusLabel(entity) {
            if (!entity?.is_active) {
                return this.$t('Deaktivierter Content');
            }

            if (!entity?.published_at || entity?.has_unpublished_changes) {
                return this.$t('Unveröffentlichter Content');
            }

            return this.$t('Veröffentlichter Content');
        },
        parseModulePath(rawValue) {
            if (typeof rawValue !== 'string') {
                return [];
            }

            return rawValue
                .split(',')
                .map((value) => Number.parseInt(String(value).trim(), 10))
                .filter((value) => Number.isInteger(value) && value > 0);
        },
        parseLegacyRouteContext(rawValue) {
            const visited = new Set();
            const modulePath = [];
            let cursor = String(rawValue || '').trim();
            let originPath = '/workspace/cms/module-library';

            while (cursor !== '' && !visited.has(cursor)) {
                visited.add(cursor);

                const [pathPart, queryString = ''] = cursor.split('?');
                const params = new URLSearchParams(queryString);
                const nestedReturnTo = params.get('return_to');
                const moduleMatch = pathPart.match(/^\/workspace\/cms\/module-library\/(\d+)\/edit$/);

                if (moduleMatch) {
                    const moduleId = Number.parseInt(moduleMatch[1], 10);
                    if (Number.isInteger(moduleId) && moduleId > 0) {
                        modulePath.push(moduleId);
                    }

                    if (nestedReturnTo) {
                        cursor = nestedReturnTo.trim();
                        continue;
                    }

                    originPath = '/workspace/cms/module-library';
                    break;
                }

                originPath = queryString ? `${pathPart}?${queryString}` : pathPart;
                break;
            }

            return {
                originPath,
                modulePath,
            };
        },
        normalizeRouteQuery() {
            if (!this.isEdit || typeof this.$route.query.return_to !== 'string' || this.$route.query.return_to.trim() === '') {
                return;
            }

            const context = this.parseLegacyRouteContext(this.$route.query.return_to);
            const query = {...this.$route.query};

            delete query.return_to;

            if (context.originPath) {
                query.origin_path = context.originPath;
            }

            if (context.modulePath.length > 0) {
                query.module_path = context.modulePath.join(',');
            } else {
                delete query.module_path;
            }

            this.$router.replace({
                path: this.$route.path,
                query,
            }).catch(() => {});
        },
        loadOptions() {
            return axios.get('api/workspace/admin/cms/modules/options').then((response) => {
                this.moduleTypeOptions = Array.isArray(response.data.module_types) ? response.data.module_types : [];
                this.languageOptions = Array.isArray(response.data.languages) ? response.data.languages : [];
            });
        },
        getModule() {
            this.moduleStateResolved = false;
            this.loading = true;
            axios.get('api/workspace/admin/cms/modules/' + this.$route.params.id).then((response) => {
                this.module = this.normalizeModule(response.data);
                this.activeTranslationIndex = Math.min(this.activeTranslationIndex, Math.max(this.module.translations.length - 1, 0));
                return Promise.all([
                    this.loadOriginEntity(),
                    this.loadBreadcrumbModules(),
                ]);
            }).then(() => {
                this.moduleStateResolved = true;
                this.loading = false;
            }).catch((error) => {
                const text = error?.response?.data?.message || this.$t('Das Modul konnte nicht geladen werden');
                this.$notify({title: this.$t('Fehler'), text, type: 'error'});
                this.moduleStateResolved = true;
                this.loading = false;
            });
        },
        loadBreadcrumbModules() {
            if (this.parentModulePath.length === 0) {
                this.breadcrumbModules = [];
                return Promise.resolve();
            }

            return Promise.all(
                this.parentModulePath.map((moduleId) => {
                    return axios.get('api/workspace/admin/cms/modules/' + moduleId).then((response) => ({
                        id: Number(response.data?.id || moduleId),
                        name: String(response.data?.name || this.$t('Modul bearbeiten')),
                        is_active: typeof response.data?.is_active === 'boolean' ? response.data.is_active : true,
                        published_at: response.data?.published_at || null,
                        has_unpublished_changes: !!response.data?.has_unpublished_changes,
                    })).catch(() => ({
                        id: Number(moduleId),
                        name: this.$t('Modul bearbeiten'),
                        is_active: true,
                        published_at: null,
                        has_unpublished_changes: false,
                    }));
                })
            ).then((modules) => {
                this.breadcrumbModules = modules;
            });
        },
        loadOriginEntity() {
            const originContext = this.originContext;

            if (!originContext?.endpoint) {
                this.originEntity = null;
                return Promise.resolve();
            }

            return axios.get(originContext.endpoint).then((response) => {
                let source = response.data || {};

                if (originContext.type === 'page') {
                    source = response.data?.page || response.data || {};
                } else if (originContext.type === 'template') {
                    source = response.data?.template || response.data || {};
                } else if (originContext.type === 'module') {
                    source = response.data?.module || response.data || {};
                }

                this.originEntity = {
                    id: Number(source.id || originContext.id || 0),
                    name: String(source.name || originContext.entityFallbackLabel || ''),
                    is_active: typeof source.is_active === 'boolean' ? source.is_active : true,
                    published_at: source.published_at || null,
                    has_unpublished_changes: !!source.has_unpublished_changes,
                };
            }).catch(() => {
                this.originEntity = {
                    id: Number(originContext.id || 0),
                    name: originContext.entityFallbackLabel || '',
                    is_active: true,
                    published_at: null,
                    has_unpublished_changes: false,
                };
            });
        },
        saveModule() {
            return this.persistModule();
        },
        publishModule() {
            return this.persistModule(true);
        },
        resetModuleDraft() {
            if (this.busy) {
                return;
            }

            this.openConfirmDialog({
                title: this.$t('Auf veröffentlichte Version zurücksetzen'),
                text: this.$t('Soll der aktuelle Entwurf wirklich auf die zuletzt veröffentlichte Version zurückgesetzt werden? Nicht veröffentlichte Änderungen gehen dabei verloren.'),
                pendingAction: () => {
                    this.busy = true;
                    this.busyAction = 'reset';

                    axios.post('api/workspace/admin/cms/modules/' + this.$route.params.id + '/reset').then((response) => {
                        this.module = this.normalizeModule(response.data.module);
                        this.moduleStateResolved = true;
                        this.$notify({title: this.$t('Success'), text: this.$t('Entwurf auf veröffentlichte Version zurückgesetzt'), type: 'success'});
                    }).catch((error) => {
                        const text = error?.response?.data?.message || this.$t('Der Entwurf konnte nicht zurückgesetzt werden');
                        this.$notify({title: this.$t('Fehler'), text, type: 'error'});
                    }).finally(() => {
                        this.busy = false;
                        this.busyAction = null;
                    });
                },
            });
        },
        persistModule(publishAfterSave = false) {
            if (this.busy) {
                return Promise.resolve();
            }

            this.busy = true;
            this.busyAction = publishAfterSave ? 'publish' : 'save';

            const request = this.isEdit
                ? axios.put('api/workspace/admin/cms/modules/' + this.$route.params.id, this.module)
                : axios.post('api/workspace/admin/cms/modules', this.module);

            return request.then((response) => {
                const savedModule = response.data.module;
                this.module = this.normalizeModule(savedModule);
                this.moduleStateResolved = true;

                if (!this.isEdit) {
                    const query = {};

                    if (this.originPath) {
                        query.origin_path = this.originPath;
                    }

                    if (this.parentModulePath.length > 0) {
                        query.module_path = this.parentModulePath.join(',');
                    }

                    this.$router.push({
                        path: '/workspace/cms/module-library/' + savedModule.id + '/edit',
                        query,
                    });
                }

                if (publishAfterSave) {
                    return axios.post('api/workspace/admin/cms/modules/' + savedModule.id + '/publish').then((publishResponse) => {
                        this.module = this.normalizeModule(publishResponse.data.module);
                        this.moduleStateResolved = true;
                        this.$notify({title: this.$t('Success'), text: this.$t('Modul erfolgreich veröffentlicht'), type: 'success'});
                        return publishResponse;
                    });
                }

                this.$notify({title: this.$t('Success'), text: this.isEdit ? this.$t('Entwurf gespeichert') : this.$t('Modul angelegt'), type: 'success'});
                return response;
            }).catch((error) => {
                const text = error?.response?.data?.message || this.$t('Das Modul konnte nicht gespeichert werden');
                this.$notify({title: this.$t('Fehler'), text, type: 'error'});
                throw error;
            }).finally(() => {
                this.busy = false;
                this.busyAction = null;
            });
        },
        deleteModule() {
            this.openConfirmDialog({
                title: this.$t('Modul löschen'),
                text: this.$t('Soll dieses Modul wirklich gelöscht werden?'),
                pendingAction: () => {
                    axios.delete('api/workspace/admin/cms/modules/' + this.$route.params.id).then(() => {
                        this.$notify({title: this.$t('Success'), text: this.$t('Data deleted successfully'), type: 'success'});
                        this.$router.push(this.closeTarget);
                    });
                },
            });
        },
        normalizeModule(payload) {
            const module = {
                id: payload.id || null,
                cms_module_type_id: payload.cms_module_type_id || null,
                name: payload.name || '',
                is_global: typeof payload.is_global === 'boolean' ? payload.is_global : true,
                is_active: typeof payload.is_active === 'boolean' ? payload.is_active : true,
                settings: payload.settings || {},
                translations: Array.isArray(payload.translations) ? payload.translations : [],
                slot_groups: Array.isArray(payload.slot_groups) ? payload.slot_groups : [],
                module_type_definition: payload.module_type_definition || null,
                published_at: payload.published_at || null,
                has_unpublished_changes: !!payload.has_unpublished_changes,
            };

            if (!this.isEdit) {
                module.is_global = true;
            }

            this.applySchemaDefaults(module);

            return module;
        },
        applyModuleTypeDefaults() {
            const moduleType = this.activeModuleType;
            if (!moduleType) {
                return;
            }

            this.module.module_type_definition = moduleType;
            this.module.settings = this.buildValuesFromSchema(moduleType.settings_schema || [], this.module.settings);
            this.module.translations = this.languageOptions.map((language) => ({
                id: null,
                cms_language_id: language.id,
                values: this.buildValuesFromSchema(moduleType.translation_schema || [], {}),
                is_active: !!language.is_active,
                language: {
                    id: language.id,
                    locale: language.locale,
                    name: language.name,
                    is_default: !!language.is_default,
                },
            }));
            this.activeTranslationIndex = 0;
        },
        applySchemaDefaults(module) {
            const moduleType = module.module_type_definition || this.activeModuleType;
            module.settings = this.buildValuesFromSchema(moduleType?.settings_schema || [], module.settings || {});

            module.translations = (Array.isArray(module.translations) ? module.translations : []).map((translation) => {
                const language = translation.language || this.languageOptions.find((item) => item.id === translation.cms_language_id) || null;

                return {
                    ...translation,
                    values: this.buildValuesFromSchema(moduleType?.translation_schema || [], translation.values || {}),
                    is_active: typeof translation.is_active === 'boolean' ? translation.is_active : !!language?.is_active,
                    language: language ? {
                        id: language.id,
                        locale: language.locale,
                        name: language.name,
                        is_default: !!language.is_default,
                    } : null,
                };
            });
        },
        buildValuesFromSchema(schema, existingValues) {
            const values = {...(existingValues || {})};

            (Array.isArray(schema) ? schema : []).forEach((field) => {
                if (!field || !field.key) {
                    return;
                }

                if (typeof values[field.key] === 'undefined') {
                    values[field.key] = this.defaultFieldValue(field);
                }
            });

            return values;
        },
        defaultFieldValue(field) {
            if (Object.prototype.hasOwnProperty.call(field || {}, 'default')) {
                return field.default;
            }

            if (field.type === 'checkbox' || field.type === 'switch') {
                return false;
            }

            return '';
        },
        fieldOptions(field) {
            const options = field?.attributes?.options || field?.options || [];

            return (Array.isArray(options) ? options : []).map((option) => {
                if (typeof option === 'object') {
                    return {
                        value: option.value,
                        label: option.label || option.value,
                    };
                }

                return {
                    value: option,
                    label: option,
                };
            });
        },
        fieldRows(field, fallback) {
            return field?.attributes?.rows || fallback;
        },
        fieldMinHeight(field, fallback) {
            const rows = Number(field?.attributes?.rows || 0);
            return rows > 0 ? rows * 20 : fallback;
        },
        isWrapperClassField(field) {
            return String(field?.key || '').trim() === 'wrapper_class';
        },
        fieldColumnClass(field) {
            return ['textarea', 'wysiwyg', 'html', 'file_picker'].includes(field.type) || this.isWrapperClassField(field)
                ? 'xl:col-span-2'
                : '';
        },
        fieldEditorId(scope, fieldKey) {
            return `cms-module-${scope}-${fieldKey}`.replace(/[^a-zA-Z0-9_-]/g, '-');
        },
        translationLabel(translation, index) {
            return translation.language?.locale || `${this.$t('Sprache')} ${index + 1}`;
        },
        openConfirmDialog({title, text, pendingAction}) {
            this.confirmDialog = {
                open: true,
                title,
                text,
                pendingAction,
            };
        },
        closeConfirmDialog() {
            this.confirmDialog = {
                open: false,
                title: '',
                text: '',
                pendingAction: null,
            };
        },
        confirmDialogAction() {
            const action = this.confirmDialog.pendingAction;
            this.closeConfirmDialog();
            if (typeof action === 'function') {
                action();
            }
        },
    },
}
</script>

<style scoped>
.cms-translation-content {
    padding: 0;
}

.cms-translation-meta {
    padding: 1.5rem 1.5rem 0;
}
</style>
